import React, { useState } from "react"
import {
	Alert,
	Button,
	Input,
	Modal,
	Radio,
	Select,
	Tag,
	notification,
} from "antd"
import { Icon } from "components"
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"
import ClassificationPresetSelector, {
	describeDimension,
	getOrderDimension,
	suggestDimension,
	BAG_DIMENSIONS,
	OUT_OF_FORMAT_DIMENSIONS,
} from "../ClassificationPresetSelector"

interface PackagingEditorProps {
	order: any
	setOrders: React.Dispatch<React.SetStateAction<any[]>>
	onBack: () => void
}

type Mode = "preset" | "manual"

const PRESET_DIMENSIONS = [...BAG_DIMENSIONS, ...OUT_OF_FORMAT_DIMENSIONS]

const PackagingEditor: React.FC<PackagingEditorProps> = ({
	order,
	setOrders,
	onBack,
}) => {
	const orderFlow = order.orderFlow || {}
	const editable = orderFlow.status === "packing"
	const currentDimension = getOrderDimension(orderFlow)
	const suggestion = suggestDimension(order.packageSize)
	const isBulk = orderFlow.classificationSource === "bulk"

	// Several boxes or an id outside the presets can only be kept in manual mode.
	const fitsPreset =
		(orderFlow.boxes?.length ?? 0) <= 1 &&
		(!currentDimension || PRESET_DIMENSIONS.includes(currentDimension))

	const [mode, setMode] = useState<Mode>(fitsPreset ? "preset" : "manual")
	const [dimension, setDimension] = useState<string>(
		(fitsPreset && currentDimension) || suggestion || "#3"
	)

	const initialType = orderFlow.boxes?.length > 0 ? "box" : "bag"
	const [packingType, setPackingType] = useState<"bag" | "box">(initialType)
	const [bagId, setBagId] = useState(orderFlow.bagId || "")
	const [bagWeight, setBagWeight] = useState<string>(
		orderFlow.bagWeight != null ? String(orderFlow.bagWeight) : ""
	)
	const [boxes, setBoxes] = useState<{ id: string; weight: string }[]>(
		orderFlow.boxes?.length > 0
			? orderFlow.boxes.map((b: any) => ({
					id: b.id || "",
					weight: b.weight != null ? String(b.weight) : "",
				}))
			: [{ id: "", weight: "" }]
	)
	const [saving, setSaving] = useState(false)

	const handlePackingTypeChange = (value: "bag" | "box") => {
		setPackingType(value)
		if (value === "box" && boxes.length === 0) {
			setBoxes([{ id: "", weight: "" }])
		}
	}

	const updateBox = (index: number, field: "id" | "weight", value: string) => {
		setBoxes((prev) =>
			prev.map((box, i) => (i === index ? { ...box, [field]: value } : box))
		)
	}

	const removeBox = (index: number) => {
		setBoxes((prev) => prev.filter((_, i) => i !== index))
	}

	const addBox = () => {
		setBoxes((prev) => [...prev, { id: "", weight: "" }])
	}

	const warn = (description: string) =>
		notification.warn({
			message: Strings.sidebar.orders,
			description,
			placement: "bottomRight",
			duration: 5,
		})

	const validateManual = () => {
		if (packingType === "bag") {
			if (!bagId.trim() || !bagWeight) return Strings.orders.fillBagFields
			if (!bagId.trim().startsWith("#"))
				return Strings.orders.bagIdMustStartWithHash
		} else {
			if (boxes.length === 0) return Strings.orders.fillOneBox
			if (boxes.some((box) => !box.id.trim() || !box.weight))
				return Strings.orders.boxMissingFields
		}
		return null
	}

	// What the API wrote, so the table updates without a refetch.
	const nextOrder = () => {
		let flow: any
		if (mode === "preset") {
			flow = dimension.startsWith("#")
				? { bagId: `${dimension}${order.orderId}`, boxes: [] }
				: {
						bagId: null,
						bagWeight: null,
						boxes: [{ id: `${dimension}${order.orderId}`, weight: 1 }],
					}
		} else if (packingType === "bag") {
			flow = {
				bagId: bagId.trim(),
				bagWeight: parseFloat(bagWeight),
				boxes: [],
			}
		} else {
			flow = {
				bagId: null,
				bagWeight: null,
				boxes: boxes.map((b) => ({
					id: b.id.trim(),
					weight: parseFloat(b.weight),
				})),
			}
		}
		const outOfFormat = flow.boxes.some((b: any) => b.id.startsWith("X"))
		return {
			...order,
			...(outOfFormat ? { onlyBike: false } : {}),
			orderFlow: {
				...orderFlow,
				...flow,
				classificationSource: "manual",
				bulkClassificationId: null,
			},
		}
	}

	const submit = async (forceReplace = false): Promise<void> => {
		const data =
			mode === "preset"
				? { mode, dimension, forceReplace }
				: packingType === "bag"
					? { mode, bagId: bagId.trim(), bagWeight: parseFloat(bagWeight) }
					: {
							mode,
							boxes: boxes.map((b) => ({
								id: b.id.trim(),
								weight: parseFloat(b.weight),
							})),
						}

		setSaving(true)
		try {
			const response = await API.post({
				url: Endpoints.uriOrderClassification(order._id),
				data,
			})
			const result = response.data?.results

			if (response.ok && result?.requiresConfirmation) {
				setSaving(false)
				Modal.confirm({
					title: Strings.orders.alreadyClassifiedTitle,
					content: Strings.orders.alreadyClassifiedContent,
					okText: Strings.orders.replace,
					cancelText: Strings.generic.cancel,
					onOk: () => submit(true),
				})
				return
			}

			if (response.ok && result?.success) {
				const updated = nextOrder()
				setOrders((prev) =>
					prev.map((o) => (o._id === order._id ? updated : o))
				)
				notification.success({
					message: Strings.sidebar.orders,
					description: Strings.orders.packaging,
					placement: "bottomRight",
					duration: 5,
				})
				onBack()
				return
			}

			notification.error({
				message: Strings.sidebar.orders,
				description:
					result?.message ||
					response.data?.message ||
					Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		} catch {
			notification.error({
				message: Strings.sidebar.orders,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		} finally {
			setSaving(false)
		}
	}

	const handleSave = () => {
		if (mode === "manual") {
			const problem = validateManual()
			if (problem) return warn(problem)
		}
		submit()
	}

	return (
		<>
			<div className="OrderDetailPanel__header">
				<div className="OrderDetailPanel__title">
					<Button type="text" icon={<Icon name="back" />} onClick={onBack} />
					<h3>{Strings.orders.packaging}</h3>
				</div>
			</div>

			<div className="OrderDetailPanel__content">
				<div className="PackagingEditor__field">
					<label className="InputLabel">
						{Strings.orders.currentClassification}
					</label>
					<div>
						{describeDimension(currentDimension)}
						{isBulk && (
							<Tag color="orange" style={{ marginLeft: 8 }}>
								{Strings.orders.bulkClassificationShort}
							</Tag>
						)}
					</div>
					{suggestion && (
						<div style={{ color: "#1890ff", fontSize: 12, marginTop: 4 }}>
							{Strings.orders.suggestedClassification}:{" "}
							{describeDimension(suggestion)}
						</div>
					)}
				</div>

				{!editable && (
					<Alert
						type="info"
						showIcon
						style={{ marginBottom: 16 }}
						message={Strings.orders.classificationReadOnly}
					/>
				)}

				<div className="PackagingEditor__field">
					<Radio.Group
						style={{
							display: "grid",
							gridTemplateColumns: "1fr 1fr",
							width: "100%",
						}}
						value={mode}
						disabled={!editable}
						onChange={(e) => setMode(e.target.value)}
					>
						<Radio.Button value="preset" style={{ textAlign: "center" }}>
							{Strings.orders.classificationPreset}
						</Radio.Button>
						<Radio.Button value="manual" style={{ textAlign: "center" }}>
							{Strings.orders.classificationManual}
						</Radio.Button>
					</Radio.Group>
				</div>

				{mode === "preset" ? (
					<div className="PackagingEditor__field">
						<ClassificationPresetSelector
							value={dimension}
							onChange={setDimension}
							disabled={!editable}
						/>
					</div>
				) : (
					<>
						<div className="PackagingEditor__field">
							<label className="InputLabel">
								{Strings.orders.packagingType}
							</label>
							<Select
								style={{ width: "100%" }}
								value={packingType}
								disabled={!editable}
								onChange={handlePackingTypeChange}
							>
								<Select.Option value="bag">{Strings.orders.bag}</Select.Option>
								<Select.Option value="box">{Strings.orders.box}</Select.Option>
							</Select>
						</div>

						{packingType === "bag" ? (
							<>
								<div className="PackagingEditor__field">
									<label className="InputLabel">{Strings.orders.bagId}</label>
									<Input
										placeholder={Strings.orders.bagId}
										value={bagId}
										disabled={!editable}
										onChange={(e) => setBagId(e.target.value)}
									/>
								</div>
								<div className="PackagingEditor__field">
									<label className="InputLabel">{Strings.orders.weight}</label>
									<Input
										placeholder={Strings.orders.weight}
										value={bagWeight}
										type="number"
										min={0}
										step="0.001"
										suffix="Kg"
										disabled={!editable}
										onChange={(e) => setBagWeight(e.target.value)}
									/>
								</div>
							</>
						) : (
							<>
								{boxes.map((box, index) => (
									<div className="PackagingEditor__box-entry" key={index}>
										<div className="PackagingEditor__box-header">
											<span>
												{Strings.orders.box} #{index + 1}
											</span>
											{boxes.length > 1 && editable && (
												<Button
													type="text"
													size="small"
													danger
													icon={<Icon name="trash" />}
													onClick={() => removeBox(index)}
												/>
											)}
										</div>
										<div className="PackagingEditor__box-fields">
											<div className="PackagingEditor__field">
												<label className="InputLabel">
													{Strings.orders.boxId}
												</label>
												<Input
													placeholder={Strings.orders.boxId}
													value={box.id}
													disabled={!editable}
													onChange={(e) =>
														updateBox(index, "id", e.target.value)
													}
												/>
											</div>
											<div className="PackagingEditor__field">
												<label className="InputLabel">
													{Strings.orders.weight}
												</label>
												<Input
													placeholder={Strings.orders.weight}
													value={box.weight}
													type="number"
													min={0}
													step="0.001"
													suffix="Kg"
													disabled={!editable}
													onChange={(e) =>
														updateBox(index, "weight", e.target.value)
													}
												/>
											</div>
										</div>
									</div>
								))}
								{editable && (
									<Button
										type="dashed"
										block
										onClick={addBox}
										className="PackagingEditor__add-box"
									>
										{Strings.orders.addBox}
									</Button>
								)}
							</>
						)}
					</>
				)}

				{editable && (
					<div className="PackagingEditor__footer">
						<Button type="primary" block loading={saving} onClick={handleSave}>
							{Strings.generic.save}
						</Button>
					</div>
				)}
			</div>
		</>
	)
}

export default PackagingEditor
